import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Empty } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

function GroupCard({ g, onDone }) {
  const [ticked, setTicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      onDone(g.key);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  const toggle = (id) => {
    setTicked((current) => (
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    ));
  };
  const deleteTicked = () => run(
    () => api.post("/practicals/bulk-delete", { ids: ticked }),
    `${ticked.length} practical${ticked.length === 1 ? "" : "s"} deleted`,
  );
  return (
    <div className="rounded-xl border bg-card" data-testid={`dup-group-${g.items[0].id}`}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b">
        <span className="text-xs text-muted-foreground">
          {g.items.length} similar names · <span className="font-medium text-foreground">{g.similarity}% match</span>
        </span>
        <span className="text-[11px] text-muted-foreground">Tick records to delete</span>
      </div>
      <div className="divide-y">
        {g.items.map((i) => (
          <label
            key={i.id}
            className={`flex items-start gap-3 px-4 py-3 cursor-pointer ${
              ticked.includes(i.id) ? "bg-destructive/5" : ""
            }`}
            data-testid={`dup-item-${i.id}`}
          >
            <Checkbox
              checked={ticked.includes(i.id)}
              onCheckedChange={() => toggle(i.id)}
              className="mt-1"
              aria-label={`Delete ${i.name}`}
              data-testid={`dup-delete-${i.id}`}
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">{i.name}</div>
              <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
                <span>{i.subject || "No subject"}</span>
                <span>{i.year_groups.length ? `Y${i.year_groups.join("/")}` : "No year"}</span>
                <span>{i.equipment_count} equipment</span>
                {i.has_risk_assessment && <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" />RA ref</span>}
                <span>{i.review_status}</span>
                <span>Added {fmt(i.created_at)}</span>
              </div>
            </div>
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 px-4 py-3 border-t">
        <Button
          size="sm"
          variant="destructive"
          onClick={deleteTicked}
          disabled={busy || !ticked.length}
          data-testid={`dup-delete-ticked-${g.items[0].id}`}
        >
          <Trash2 className="mr-1.5 h-3.5 w-3.5" />
          Delete ticked{ticked.length ? ` (${ticked.length})` : ""}
        </Button>
      </div>
    </div>
  );
}

export function DuplicatesView({ onChanged }) {
  const [groups, setGroups] = useState(null);
  const load = useCallback(() => {
    api.get("/practicals-duplicates").then((r) => setGroups(r.data.groups));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  if (!groups) return <p className="text-sm text-muted-foreground">Looking for duplicates…</p>;
  const done = (key) => {
    setGroups((gs) => gs.filter((g) => g.key !== key));
    onChanged?.();
  };
  return (
    <div className="space-y-3" data-testid="duplicates-view">
      <p className="text-sm text-muted-foreground max-w-3xl">
        Practicals with very similar names, ignoring class codes like “8Gc 1:”, “BG#” or
        “*COPY*”. Tick only the records to remove; all unticked practicals stay in the library.
      </p>
      {groups.length === 0 ? <Empty>No likely duplicates found.</Empty> : groups.map((g) => <GroupCard key={g.key} g={g} onDone={done} />)}
    </div>
  );
}
